import React, { useMemo, useEffect, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQuery } from "react-query";

import { FabList, Button } from "components";
import { BrandingColorContext } from "components/contexts";
import { FormContainer, FormContent } from "components/layout";
import {
	feesModel,
	pricingMethods,
	scheduleAFormDefaultValues,
	scheduleAStatuses,
} from "models";
import { scheduleAService, staticDropdownValuesService } from "services";
import {
	BusinessUnitTypeObsolete,
	PricingMethodType,
	ScheduleAType,
} from "types";
import { float4Normalizer } from "validators";

import { FeesBlock } from "./feesBlock";
import { ScheduleAMainFields } from "./scheduleAMainFields";

import { ReactComponent as SaveIcon } from "static/images/save.svg";
import styles from "./scheduleA.module.scss";

type ScheduleAFormProps = {
	isEditing?: boolean;
	isLoading?: boolean;
	isAgentLoading: boolean;
	isGettingSchedule?: boolean;
	userId: string;
	agentName: string;
	onSubmit: Function;
	scheduleData?: ScheduleAType;
	agentOfficeId: number;
	agentBusinessUnit?: string;
};

const ScheduleAForm = ({
	isEditing = false,
	isLoading = false,
	isAgentLoading,
	isGettingSchedule = false,
	userId,
	scheduleData,
	onSubmit,
	agentName,
	agentOfficeId,
	agentBusinessUnit,
}: ScheduleAFormProps) => {
	const [isBusinessUnitAuroraOrAMS, setIsBusinessUnitAuroraOrAMS] =
		useState(false);

	const formMethods = useForm({
		defaultValues: scheduleAFormDefaultValues,
		mode: "all",
		reValidateMode: "onChange",
		shouldUnregister: false,
	});

	const { reset, watch, handleSubmit, getValues } = formMethods;

	const { data: pricingMethodsArr } = useQuery<PricingMethodType[]>(
		"pricingMethods",
		staticDropdownValuesService.getPricingMethods,
	);
	const watchAreFeesNotApplicable = watch("are_fees_not_applicable");

	const parentScheduleA = watch("parent", getValues("parent"));

	const isParentScheduleA = useMemo(
		() => !!parentScheduleA?.value,
		[parentScheduleA?.value],
	);

	const [prevParentScheduleAId, setPrevParentScheduleAId] =
		useState<string>("");

	const {
		data: parentScheduleAData,
		isLoading: isLoadingParentScheduleAData,
	} = useQuery(
		["parentScheduleAInfo", parentScheduleA, agentOfficeId],
		() =>
			scheduleAService.getCompanyScheduleA({
				companyId: agentOfficeId,
				scheduleId: parentScheduleA?.value,
			}),
		{
			enabled: isParentScheduleA && !!agentOfficeId,
		},
	);

	const getPricingMethod = useCallback(
		pricingMethod =>
			pricingMethodsArr?.find(({ value }) => value === pricingMethod),
		[pricingMethodsArr],
	);

	const getScheduleData = useCallback(
		scheduleData => {
			const status = scheduleAStatuses?.find(
				({ value }) => value === scheduleData?.status,
			);

			const fees = scheduleData.are_fees_not_applicable
				? feesModel
				: scheduleAFormDefaultValues.fees.map(
						({
							feeLabel,
							feeName,
							calculationLabel,
							perItemFeeValidate,
							isRevenueShareDisabled = false,
						}) => {
							const getInitialType = feeName =>
								feesModel.find(
									item => item.feeName === feeName,
								);
							return {
								feeLabel,
								feeName,
								calculationLabel,
								isRevenueShareDisabled,
								perItemFeeValidate,
								type:
									scheduleData?.fees[feeName]?.type ||
									getInitialType(feeName)?.type,
								per_item_fee: float4Normalizer(
									scheduleData?.fees[feeName]?.per_item_fee,
								),
								rate: float4Normalizer(
									scheduleData?.fees[feeName]?.rate,
								),
								revenue_share:
									scheduleData?.fees[feeName]?.revenue_share,
								pricing_method:
									getPricingMethod(
										scheduleData?.fees[feeName]
											?.pricing_method,
									) ?? pricingMethods.passThrough, // SDI-105: set by default if not found
								isPricingMethodDisabled:
									getInitialType(feeName)
										?.isPricingMethodDisabled,
							};
						},
				  );

			const parent = {
				value: scheduleData?.parent?.id,
				name: scheduleData?.parent?.name,
				status: scheduleData?.parent?.status,
			};

			return {
				...scheduleData,
				parent,
				status,
				is_default: scheduleData?.is_default || false,
				fees,
			};
		},
		[getPricingMethod],
	);

	useEffect(() => {
		if (!scheduleData) {
			setIsBusinessUnitAuroraOrAMS(
				[
					BusinessUnitTypeObsolete.AMS.name,
					BusinessUnitTypeObsolete.AuroraBusinessDevelopment.name,
				].includes(
					BusinessUnitTypeObsolete.getByName(agentBusinessUnit)
						?.name || "",
				),
			);
		}
	}, [agentBusinessUnit, getValues, scheduleData]);

	useEffect(() => {
		if (scheduleData) {
			const mappedScheduleData = getScheduleData(scheduleData);

			reset(mappedScheduleData);
		}
	}, [
		reset,
		scheduleData,
		pricingMethodsArr,
		getPricingMethod,
		getScheduleData,
	]);

	useEffect(() => {
		if (parentScheduleAData?.id !== prevParentScheduleAId) {
			setPrevParentScheduleAId(parentScheduleAData?.id);

			if (parentScheduleAData) {
				const current = getValues() as any;

				const { status, fees, processor, are_fees_not_applicable } =
					getScheduleData(parentScheduleAData);

				reset({
					name: current?.name, // do not overwrite already saved name
					is_default: current?.is_default,
					status,
					fees,
					processor,
					are_fees_not_applicable,
					parent: parentScheduleA,
				});
			}
		}
	}, [
		agentOfficeId,
		getScheduleData,
		parentScheduleA,
		parentScheduleAData,
		prevParentScheduleAId,
		reset,
		getValues,
	]);

	const submit = () => {
		const data = getValues() as any;
		const fees = data?.fees.reduce(
			(
				acc,
				{
					feeName,
					per_item_fee,
					pricing_method,
					rate,
					revenue_share,
					type,
				},
			) => ({
				...acc,
				[feeName]: {
					per_item_fee,
					pricing_method: pricing_method.value,
					rate,
					revenue_share,
					type,
				},
			}),
			{},
		);

		const payload = {
			name: data.name,
			status: data.status?.value,
			is_default: data?.is_default,
			processor_id: data.processor?.id,
			parent_id: data.parent?.value,
			are_fees_not_applicable: data.are_fees_not_applicable,
			fees: data.are_fees_not_applicable ? null : fees,
		};

		onSubmit({
			userId,
			data: payload,
			scheduleId: scheduleData?.id,
		});
	};

	const areFeesNotApplicable = useMemo(
		() => watchAreFeesNotApplicable === false,
		[watchAreFeesNotApplicable],
	);
	const feesBlockClass =
		areFeesNotApplicable && !isGettingSchedule ? "d-block" : "d-none";

	return (
		<FormProvider {...formMethods}>
			<BrandingColorContext.Provider value={true}>
				<form onSubmit={handleSubmit(submit)}>
					<FormContent>
						<FormContainer className={styles.formContainer}>
							<ScheduleAMainFields
								isEditing={isEditing}
								isLoading={isLoading}
								isParentScheduleA={isParentScheduleA}
								scheduleData={scheduleData}
								agentName={agentName}
								isAgentLoading={isAgentLoading}
								agentOfficeId={agentOfficeId}
							/>
							<div className={feesBlockClass}>
								<FeesBlock
									isDisabled={isParentScheduleA}
									isLoading={
										isLoading ||
										isLoadingParentScheduleAData
									}
									isBusinessUnitAuroraOrAMS={
										isBusinessUnitAuroraOrAMS
									}
								/>
							</div>

							<FabList>
								<Button
									type="submit"
									value="Save"
									icon={<SaveIcon />}
									size="lg"
									shape="circle"
									isLoading={isLoading}
								/>
							</FabList>
						</FormContainer>
					</FormContent>
				</form>
			</BrandingColorContext.Provider>
		</FormProvider>
	);
};

export { ScheduleAForm };
